import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Star, Send, Loader2, Check, Heart, EyeOff } from "lucide-react";
import gustoAvatar from "@/assets/gusto-avatar.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";

const CRITERIA = [
  { key: "taste", label: "Goût", emoji: "👅" },
  { key: "balance", label: "Équilibre", emoji: "⚖️" },
  { key: "satiety", label: "Satiété", emoji: "🍽️" },
  { key: "presentation", label: "Présentation", emoji: "🎨" },
];

const StarRow = ({ label, emoji, value, onChange }) => (
  <div className="flex items-center gap-3">
    <span className="text-lg w-6 text-center">{emoji}</span>
    <span className="text-sm font-medium text-charcoal w-28">{label}</span>
    <div className="flex gap-1 flex-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              s <= value ? "fill-amber-400 text-amber-400" : "text-gray-200 hover:text-amber-200"
            }`}
          />
        </button>
      ))}
    </div>
  </div>
);

const MealRatingPage = () => {
  const { invitationId } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [scores, setScores] = useState({ taste: 0, balance: 0, satiety: 0, presentation: 0 });
  const [comment, setComment] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const allScored = Object.values(scores).every((v) => v > 0);
  const avgScore = allScored ? (Object.values(scores).reduce((a, b) => a + b, 0) / 4).toFixed(1) : null;

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const res = await axios.get(`${API}/meal-invitations/${invitationId}`);
        setInvitation(res.data);
      } catch {
        setError("Cette invitation n'existe pas ou a expiré.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvitation();
  }, [invitationId]);

  const handleSubmit = async () => {
    if (!guestName.trim() || !allScored) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/meal-invitations/${invitationId}/rate`, {
        guest_name: guestName.trim(),
        taste: scores.taste,
        balance: scores.balance,
        satiety: scores.satiety,
        presentation: scores.presentation,
        comment: comment.trim() || null,
        is_anonymous: isAnonymous,
      });
      setSubmitted(true);
    } catch {
      setError("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center space-y-4">
            <img src={gustoAvatar} alt="Gusto" className="h-10 mx-auto opacity-40" />
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md w-full">
          <Card className="shadow-xl overflow-hidden">
            <div className="bg-gradient-to-br from-primary to-red-500 p-8 text-center text-white">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}>
                <div className="w-20 h-20 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-4">
                  <Check className="w-10 h-10" />
                </div>
              </motion.div>
              <h2 className="font-heading text-2xl font-bold">Merci {guestName} !</h2>
              <p className="text-white/80 mt-2">Votre avis a été envoyé à {invitation.chef_name}</p>
            </div>
            <CardContent className="p-6 text-center">
              <p className="font-heading text-3xl font-bold text-charcoal mb-2">{avgScore}/5</p>
              <div className="flex justify-center gap-4 text-sm text-muted-foreground">
                {CRITERIA.map((c) => (
                  <span key={c.key}>{c.emoji} {scores[c.key]}</span>
                ))}
              </div>
              {comment && <p className="text-sm text-muted-foreground italic mt-3">"{comment}"</p>}
              <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
                <Heart className="w-3 h-3" /> Propulsé par Gusto
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 px-4 py-8" data-testid="meal-rating-page">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary to-red-500 flex items-center justify-center">
            <img src={gustoAvatar} alt="Gusto" className="h-8 w-auto" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-charcoal">
            {invitation.chef_name} vous invite
          </h1>
          <p className="text-muted-foreground">
            à noter son plat <strong className="text-charcoal">"{invitation.recipe_name}"</strong>
          </p>
          {invitation.message && (
            <p className="text-sm italic text-muted-foreground bg-white/60 rounded-xl p-3">
              "{invitation.message}"
            </p>
          )}
        </motion.div>

        {/* Rating Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="shadow-xl">
            <CardContent className="p-6 space-y-5">
              {/* Guest Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-charcoal">Votre prénom</label>
                <Input
                  placeholder="Ex: Marie"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  data-testid="guest-name-input"
                />
              </div>

              {/* 4 Criteria Rating */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-charcoal">Vos notes</label>
                <div className="space-y-3 p-4 rounded-xl bg-gray-50">
                  {CRITERIA.map((c) => (
                    <StarRow
                      key={c.key}
                      label={c.label}
                      emoji={c.emoji}
                      value={scores[c.key]}
                      onChange={(v) => setScores((prev) => ({ ...prev, [c.key]: v }))}
                    />
                  ))}
                </div>
                {avgScore && (
                  <p className="text-center text-sm font-medium text-primary">
                    Note globale : {avgScore}/5
                  </p>
                )}
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-charcoal">
                  Un petit mot <span className="text-muted-foreground">(optionnel)</span>
                </label>
                <Textarea
                  placeholder="Super repas, merci pour l'invitation !"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  data-testid="guest-comment-input"
                />
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              {/* Anonymous Toggle */}
              <div
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 cursor-pointer select-none"
                onClick={() => setIsAnonymous(!isAnonymous)}
                data-testid="anonymous-toggle"
              >
                <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${isAnonymous ? "bg-primary justify-end" : "bg-gray-300 justify-start"}`}>
                  <div className="w-5 h-5 rounded-full bg-white shadow-sm transition-all" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-charcoal flex items-center gap-1.5">
                    <EyeOff className="w-4 h-4" />
                    Noter en anonyme
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isAnonymous ? "Votre nom ne sera pas visible par le chef" : "Le chef verra votre prénom"}
                  </p>
                </div>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={!guestName.trim() || !allScored || submitting}
                className="w-full h-12 rounded-full text-base gap-2"
                data-testid="submit-rating-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                Envoyer mon avis
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
          <Heart className="w-3 h-3" /> Propulsé par Gusto
        </p>
      </div>
    </div>
  );
};

export default MealRatingPage;
